import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, type Operator, session } from '../api/client';
import { Button, ErrorNote, Field, Input } from '../components/ui';
import { useApiError } from '../i18n/useApiError';

export function LoginPage({ onSignedIn }: { onSignedIn: () => void }) {
  const { t } = useTranslation();
  const describeError = useApiError();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await api.post<{
        accessToken: string;
        refreshToken: string;
        operator: Operator;
      }>('/admin/auth/login', { email, password });
      session.save(result);
      onSignedIn();
    } catch (e) {
      setError(describeError(e, 'login.failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-full items-center justify-center bg-surface-shell p-6">
      <form
        onSubmit={submit}
        className="flex w-full max-w-sm flex-col gap-5 rounded-2xl border border-hairline bg-white p-8"
      >
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-bold tracking-[-0.5px]">{t('login.title')}</h1>
          <p className="text-xs text-muted">{t('login.subtitle')}</p>
        </div>

        <Field label={t('login.email')}>
          <Input
            type="email"
            aria-label={t('login.email')}
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </Field>
        <Field label={t('login.password')}>
          <Input
            type="password"
            aria-label={t('login.password')}
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </Field>

        {error ? <ErrorNote message={error} /> : null}

        <Button type="submit" variant="dark" disabled={busy}>
          {t(busy ? 'login.signingIn' : 'login.signIn')}
        </Button>

        {/* Never in a deployed build: this prints a working owner password on a
            publicly reachable login page. */}
        {import.meta.env.DEV ? (
          <p className="text-[11px] leading-relaxed text-faint">{t('login.devHint')}</p>
        ) : null}
      </form>
    </div>
  );
}
